import { ChangeEvent } from 'react';

import { GearbeltAssetType } from '@site-mate/sitemate-global-shared/lib';
import { Control, Path, PathValue } from 'react-hook-form';
import { z } from 'zod';

import { cn } from '@/lib/utils';
import { nonNegativeNumberString, numberString } from '@/utils/zod';

import { Combobox } from '../ui/combobox';
import { CreatableSelect } from '../ui/creatable-select';
import { FormFieldItem } from '../ui/form';
import { Input } from '../ui/input';
import { CreatableMultiSelect, MULTI_SELECT_STYLES } from '../ui/multi-select';
import { Textarea } from '../ui/textarea';

const assetFormSchema = z.object({
  internalId: z.string().min(2, {
    message: 'Required',
  }),
  type: z.enum(GearbeltAssetType, { message: 'Required' }),
  categoryId: z.string().optional(),
  manufacturer: z.string().optional(),
  model: z.string().optional(),
  class: z.string().optional(),
  year: nonNegativeNumberString.optional(),
  registration: z.string().optional(),
  serialNumber: z.string().optional(),
  engineNumber: z.string().optional(),
  vin: z.string().optional(),
  comments: z.string().optional(),
  tags: z.array(z.object({ label: z.string(), value: z.string() })).optional(),
});

const extendedAssetFormSchema = assetFormSchema.extend({
  id: z.string(),
  rate: numberString.optional(),
  locationId: z.string().optional(),
  /**
   * @deprecated Use operatedBy instead
   */
  operatorId: z.string().optional(),
  operatedBy: z.object({ label: z.string(), value: z.string(), avatarUrl: z.string().optional() }).nullish(),
  issueFormId: z.string().optional(),
  serviceFormId: z.string().optional(),
  inspectionFormId: z.string().optional(),
});

const assetSheetImportSchema = assetFormSchema.extend({
  year: z.coerce.number().nonnegative().optional(),
  chassisVin: z.string().optional(),
  tags: z.array(z.string()).optional(),
});

// Legacy schema with schedule fields - used when feature flag is off
const assetSheetImportSchemaWithLegacyFields = assetSheetImportSchema.extend({
  lastServiceDate: z.coerce.date().optional(),
  lastScheduleType: z.coerce.number().optional(),
  lastServiceReading: z.coerce.string().optional(),
});

type AssetFormSchemaType<T extends boolean = false> = T extends true ? z.infer<typeof extendedAssetFormSchema> : z.infer<typeof assetFormSchema>;

function AssetSelectField<T extends boolean = false>({
  label,
  name,
  control,
  options,
  required,
  renderItem,
  renderSelectedValue,
  onChange: onChangeOverride,
  labelSize,
  disabled,
  className,
}: {
  label: string;
  name: Path<AssetFormSchemaType<T>>;
  control: Control<AssetFormSchemaType<T>>;
  options: { label: string; value: string }[];
  required?: boolean;
  renderItem?: (option: { value: string; label: string }, isSelected: boolean) => React.ReactNode;
  renderSelectedValue?: (option: { value: string; label: string }) => React.ReactNode;
  onChange?: (value: string) => void;
  labelSize?: 'base' | 'small';
  disabled?: boolean;
  className?: string;
}) {
  return (
    <FormFieldItem<AssetFormSchemaType<T>, Path<AssetFormSchemaType<T>>>
      control={control}
      name={name}
      label={label}
      required={required}
      labelSize={labelSize}
      className={className}>
      {({ value, onChange, error }) => (
        <Combobox
          value={value as string}
          onChange={(val: string) => {
            if (onChangeOverride) {
              onChangeOverride(val);
            } else {
              onChange(val as PathValue<AssetFormSchemaType<T>, Path<AssetFormSchemaType<T>>>);
            }
          }}
          options={options}
          aria-label={label}
          className={cn('w-full', error && 'border-default-red')}
          renderItem={renderItem}
          disabled={disabled}
          renderSelectedValue={renderSelectedValue}
        />
      )}
    </FormFieldItem>
  );
}

function AssetCreatableSelectField<T extends boolean = false>({
  label,
  name,
  control,
  options,
  required,
  onChange: onChangeOverride,
  labelSize,
  disabled,
  className,
}: {
  label: string;
  name: Path<AssetFormSchemaType<T>>;
  control: Control<AssetFormSchemaType<T>>;
  options: { label: string; value: string }[];
  required?: boolean;
  onChange?: (value: string) => void;
  labelSize?: 'base' | 'small';
  disabled?: boolean;
  className?: string;
}) {
  return (
    <FormFieldItem<AssetFormSchemaType<T>, Path<AssetFormSchemaType<T>>>
      control={control}
      name={name}
      label={label}
      required={required}
      labelSize={labelSize}
      className={className}>
      {({ value, onChange, error }) => (
        <CreatableSelect<{ label: string; value: string }>
          value={options.find((option) => option.value === value) ?? undefined}
          onChange={(val) => {
            if (onChangeOverride) {
              onChangeOverride(val?.value ?? '');
            } else {
              onChange(val?.value as PathValue<AssetFormSchemaType<T>, Path<AssetFormSchemaType<T>>>);
            }
          }}
          placeholder="Select option..."
          options={options}
          className={cn('w-full', error && 'border-default-red')}
          isDisabled={disabled}
        />
      )}
    </FormFieldItem>
  );
}

function AssetInputField<T extends boolean = false>({
  label,
  name,
  control,
  placeholder,
  required,
  disabled,
  className,
  labelSize,
}: {
  label: string;
  name: Path<AssetFormSchemaType<T>>;
  control: Control<AssetFormSchemaType<T>>;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  labelSize?: 'base' | 'small';
}) {
  return (
    <FormFieldItem<AssetFormSchemaType<T>, Path<AssetFormSchemaType<T>>>
      control={control}
      name={name}
      label={label}
      required={required}
      labelSize={labelSize}
      className={className}>
      {({ value, onChange, error }) => (
        <Input
          placeholder={placeholder ?? `Enter ${label}...`}
          value={value as string}
          onChange={onChange as unknown as (e: ChangeEvent<HTMLInputElement>) => void}
          error={!!error}
          disabled={disabled}
        />
      )}
    </FormFieldItem>
  );
}

function AssetTextareaField<T extends boolean = false>({
  label,
  name,
  control,
  placeholder,
  required,
  className,
  labelSize,
  disabled,
}: {
  label: string;
  name: Path<AssetFormSchemaType<T>>;
  control: Control<AssetFormSchemaType<T>>;
  placeholder?: string;
  required?: boolean;
  className?: string;
  labelSize?: 'base' | 'small';
  disabled?: boolean;
}) {
  return (
    <FormFieldItem<AssetFormSchemaType<T>, Path<AssetFormSchemaType<T>>>
      control={control}
      name={name}
      label={label}
      required={required}
      labelSize={labelSize}
      className={className}>
      {({ value, onChange, error }) => (
        <Textarea
          className="min-h-[100px] resize-none"
          placeholder={placeholder ?? `Enter ${label}...`}
          value={value as string}
          onChange={onChange as unknown as (e: ChangeEvent<HTMLTextAreaElement>) => void}
          error={!!error}
          disabled={disabled}
        />
      )}
    </FormFieldItem>
  );
}

function AssetCreatableMultiSelectField<T extends boolean = false>({
  label,
  name,
  control,
  placeholder,
  required,
  defaultOptions,
  className,
  labelSize,
  controlRootClassName,
  controlTriggerClassName,
  disabled,
}: {
  label: string;
  name: Path<AssetFormSchemaType<T>>;
  control: Control<AssetFormSchemaType<T>>;
  placeholder?: string;
  required?: boolean;
  defaultOptions: { label: string; value: string }[];
  labelSize?: 'base' | 'small';
  className?: string;
  controlTriggerClassName?: string;
  controlRootClassName?: string;
  disabled?: boolean;
}) {
  return (
    <FormFieldItem<AssetFormSchemaType<T>, Path<AssetFormSchemaType<T>>>
      control={control}
      name={name}
      label={label}
      required={required}
      labelSize={labelSize}
      className={className}>
      {({ value, onChange }) => (
        <CreatableMultiSelect<{ label: string; value: string }>
          options={defaultOptions}
          placeholder={placeholder ?? `Enter ${label}...`}
          value={value as unknown as { label: string; value: string }[]}
          onChange={(value) => onChange(value as PathValue<AssetFormSchemaType<T>, Path<AssetFormSchemaType<T>>>)}
          isDisabled={disabled}
          classNames={(() => {
            const styles = MULTI_SELECT_STYLES<{ label: string; value: string }>();

            return {
              ...styles,
              container: (props) => cn(styles.container?.(props), controlRootClassName),
              control: (props) => cn(styles.control?.(props), controlTriggerClassName),
            };
          })()}
        />
      )}
    </FormFieldItem>
  );
}

export {
  assetFormSchema,
  extendedAssetFormSchema,
  assetSheetImportSchema,
  assetSheetImportSchemaWithLegacyFields,
  AssetCreatableSelectField,
  AssetSelectField,
  AssetInputField,
  AssetTextareaField,
  AssetCreatableMultiSelectField,
};
